<template>
  <div>
    <b-card class="main-content">
      <vue-element-loading :active="loader" />

      <template #header>
        <h4 class="mb-0">Plumm Invoice Calculator</h4>
        <small class="text-muted">Calculate prorated invoice for additional slots</small>
      </template>

      <b-row>
        <b-col md="6">
          <b-form-group label="Select Client (Optional - for new client leave empty)">
            <v-select
              v-model="selectedClient"
              :options="clientList"
              :reduce="x => x.code"
              label="description"
              placeholder="Select a client or leave empty for new client"
              :clearable="true"
              @input="onClientChange"
            >
              <template #no-options>No clients found</template>
            </v-select>
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group :label="selectedClient ? 'Additional Slots *' : 'Number of Slots *'">
            <b-input-group>
              <b-input-group-prepend>
                <b-button variant="primary" @click.stop="decrementSlot">
                  <feather-icon icon="MinusIcon" />
                </b-button>
              </b-input-group-prepend>
              <b-form-input
                v-model.number="additionalSlots"
                type="number"
                min="0"
                class="text-center"
                @input="calculate"
              ></b-form-input>
              <b-input-group-append>
                <b-button variant="primary" @click.stop="incrementSlot">
                  <feather-icon icon="PlusIcon" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Client Information Card -->
      <b-card v-if="clientInfo" class="mt-3 border-primary">
        <h5 class="mb-3">Client Information</h5>
        <b-row>
          <b-col md="4">
            <div class="d-flex align-items-center mb-2">
              <feather-icon icon="UserIcon" size="18" class="text-primary mr-1" />
              <strong>Client:</strong>
              <span class="ml-1">{{ pascalCase(clientInfo.description) }}</span>
            </div>
          </b-col>
          <b-col md="4">
            <div class="d-flex align-items-center mb-2">
              <feather-icon icon="PackageIcon" size="18" class="text-primary mr-1" />
              <strong>Current Slots:</strong>
              <span class="ml-1">{{ clientInfo.slotCount }}</span>
            </div>
          </b-col>
          <b-col md="4">
            <div class="d-flex align-items-center mb-2">
              <feather-icon icon="PlusSquareIcon" size="18" class="text-primary mr-1" />
              <strong>Total Slots:</strong>
              <span class="ml-1">{{ totalSlots }}</span>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <!-- Calculation Results Card -->
      <b-card v-if="calculationResult" class="mt-3 bg-light-success">
        <h5 class="mb-3 text-success">
          <feather-icon icon="DollarSignIcon" size="20" />
          Prorated Invoice Calculation
        </h5>

        <b-row>
          <b-col md="6">
            <div class="mb-3">
              <label class="text-muted mb-1">Calculation Period</label>
              <div class="h6 mb-0">{{ calculationResult.today }} - {{ calculationResult.endOfMonth }}</div>
            </div>
          </b-col>
          <b-col md="6">
            <div class="mb-3">
              <label class="text-muted mb-1">Days Remaining</label>
              <div class="h6 mb-0">
                {{ calculationResult.remainingDays }} / {{ calculationResult.totalDaysInMonth }} days
              </div>
            </div>
          </b-col>
        </b-row>

        <b-row class="mt-2">
          <b-col md="6">
            <div class="mb-3">
              <label class="text-muted mb-1">Price Per Slot (Monthly)</label>
              <div class="h6 mb-0">
                {{ formatCurrency(calculationResult.pricePerSlot) }}
              </div>
            </div>
          </b-col>
        </b-row>

        <hr />

        <b-row>
          <b-col md="6">
            <div class="mb-2">
              <label class="text-muted mb-1">Calculation</label>
              <div class="text-sm">
                {{ calculationResult.remainingDays }} days ÷ {{ calculationResult.totalDaysInMonth }} days ×
                {{ formatCurrency(calculationResult.pricePerSlot) }} × {{ additionalSlots }} slot{{ additionalSlots > 1 ? 's' : '' }}
              </div>
            </div>
          </b-col>
          <b-col md="6" class="text-right">
            <label class="text-muted mb-1">{{ selectedClient ? 'Prorated Amount' : 'Total Amount (New Client)' }}</label>
            <h3 class="text-success mb-0">
              {{ formatCurrency(calculationResult.proratedAmount) }}
            </h3>
          </b-col>
        </b-row>
      </b-card>

      <!-- Empty State -->
      <b-card v-else class="mt-3 text-center border-0 bg-light">
        <feather-icon icon="CalculatorIcon" size="48" class="text-muted mb-2" />
        <p class="text-muted mb-0">Enter number of slots to calculate prorated invoice</p>
        <small class="text-muted">You can select an existing client or calculate for a new client</small>
      </b-card>
    </b-card>
  </div>
</template>

<script>
import vSelect from "vue-select";
import api from "@api";
import { mapState } from "vuex";

export default {
  name: "PlummInvoiceCalculator",
  components: {
    vSelect,
  },
  data() {
    return {
      selectedClient: null,
      clientList: [],
      clientInfo: null,
      additionalSlots: 0,
      calculationResult: null,
      loader: false,
      // Pricing tiers in USD
      pricingTiers: [
        { minSlots: 1, maxSlots: 4, pricePerSlot: 20 }, // Tier 1: < 5 slots = $20 each
        { minSlots: 5, maxSlots: 19, pricePerSlot: 18 }, // Tier 2: 5-19 slots = $18 each
        { minSlots: 20, maxSlots: 999999, pricePerSlot: 15 }, // Tier 3: >= 20 slots = $15 each
      ],
    };
  },
  computed: {
    ...mapState(`auth`, [`user`]),
    totalSlots() {
      // If no client selected, total = additionalSlots (new client scenario)
      if (!this.clientInfo) return this.additionalSlots;
      // If client selected, total = current + additional
      return this.clientInfo.slotCount + this.additionalSlots;
    },
  },
  async mounted() {
    await this.loadClients();
  },
  methods: {
    async loadClients() {
      this.loader = true;
      try {
        if (this.user.roleCode === 'SALES') {
          // Sales users see only their assigned clients
          this.clientList = await api.get(`client/by-sales/${this.user.userCode}`);
        } else {
          // Admin and other roles see all clients
          this.clientList = await api.get("client");
        }
      } catch (error) {
        this.showToast("error", error);
      } finally {
        this.loader = false;
      }
    },
    async onClientChange() {
      if (!this.selectedClient) {
        this.clientInfo = null;
        // Still allow calculation for new clients
        this.calculate();
        return;
      }

      this.loader = true;
      try {
        // Get client basic info and slot count from API
        const [clientData, slotData] = await Promise.all([
          api.get(`client/${this.selectedClient}`),
          api.get(`slot/slot-count/${this.selectedClient}`),
        ]);

        this.clientInfo = {
          ...clientData,
          slotCount: slotData,
        };

        this.calculate();
      } catch (error) {
        this.showToast("error", error);
      } finally {
        this.loader = false;
      }
    },
    calculate() {
      // Allow calculation without client (for new clients) as long as slots > 0
      if (this.additionalSlots <= 0) {
        this.calculationResult = null;
        return;
      }

      // Get today's date
      const today = new Date();

      // Get the end of the month
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

      // Calculate total days in current month
      const totalDaysInMonth = endOfMonth.getDate();

      // Calculate remaining days (including today)
      const remainingDays = endOfMonth.getDate() - today.getDate() + 1;

      // Calculate prorate factor
      const prorateFactor = remainingDays / totalDaysInMonth;
      const proratePercentage = (prorateFactor * 100).toFixed(2);

      // Get price per slot based on total slots (current + additional) to determine tier
      // If no client selected, totalSlots = additionalSlots (new client scenario)
      const pricePerSlot = this.getPricePerSlot(this.totalSlots);

      // Calculate prorated amount
      const proratedAmount = prorateFactor * pricePerSlot * this.additionalSlots;

      this.calculationResult = {
        today: this.formatDate(today),
        endOfMonth: this.formatDate(endOfMonth),
        totalDaysInMonth,
        remainingDays,
        prorateFactor,
        proratePercentage,
        pricePerSlot,
        proratedAmount,
      };
    },
    getPricePerSlot(totalSlots) {
      const tier = this.pricingTiers.find(t => totalSlots >= t.minSlots && totalSlots <= t.maxSlots);
      return tier ? tier.pricePerSlot : this.pricingTiers[0].pricePerSlot;
    },
    formatDate(date) {
      const options = { year: "numeric", month: "short", day: "numeric" };
      return date.toLocaleDateString("en-US", options);
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      }).format(amount);
    },
    incrementSlot() {
      this.additionalSlots++;
      this.calculate();
    },
    decrementSlot() {
      if (this.additionalSlots > 0) {
        this.additionalSlots--;
        this.calculate();
      }
    },
  },
};
</script>

<style scoped>
.text-sm {
  font-size: 0.875rem;
}
</style>
